import { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual } from 'react-redux';
import get from 'lodash/get';
import plur from 'plur';
import snakeCase from 'lodash/snakeCase';
import toUpper from 'lodash/toUpper';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Table from 'components/table';
import { useDispatch, useListParams, useSelector } from 'hooks';
import TableRow from 'components/table/TableRow';
import * as Actions from '@/redux/actions';
import * as Constants from '@/redux/constants';
import * as Models from 'models';
import { uncapitalize } from 'lib/utils';

function DialogSelect({ open, onClose, model, payload, onSubmit }) {
  const listParams = useListParams();
  const { itemsInfo, itemInfo } = useSelector((state) => ({
    itemsInfo: get(state, `${plur(uncapitalize(model))}Info`),
    itemInfo: get(state, `${uncapitalize(model)}Info`),
  }), shallowEqual);
  const dispatch = useDispatch();
  const isFetching = itemsInfo.isFetching || itemInfo.isFetching;
  const search = listParams.getSearch();
  const selected = listParams.getSelected();
  const headCells = useMemo(() => {
    return Models[model].headCells.filter((cell) => cell.type !== 'actions');
  }, []);

  const fetchData = useCallback(() => {
    if (!open) {
      return;
    }
    dispatch(Actions[`read${plur(model)}`]({
      payload: {
        ...listParams.data,
        ...payload,
      },
    }));
  }, [open, dispatch, model, listParams.data]);
  useEffect(fetchData, [fetchData]);
  useEffect(() => {
    const con = toUpper(snakeCase(model));
    switch (itemInfo.type) {
      case Constants[`CREATE_${con}`].SUCCESS:
      case Constants[`UPDATE_${con}`].SUCCESS:
      case Constants[`DELETE_${con}`].SUCCESS:
        fetchData();
        break;
    }
  }, [itemInfo.type, model]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) {
      listParams.clear();
    }
  }, [open]);

  const onChangeSearchHandler = useCallback((e) => listParams.setSearch(e.target.value), [listParams.setSearch]);
  const onSubmitHandler = useCallback((e) => {
    e.preventDefault();
    onSubmit(selected.first());
  }, [onSubmit, selected]);
  const renderRow = useCallback(({ isItemSelected, labelId, row, handleClick }, index) => {
    return (
      <TableRow
        key={`table-row-${index}`}
        headCells={headCells}
        row={row}
        isItemSelected={isItemSelected}
        labelId={labelId}
        onSelect={handleClick}
        showSelect
      />
    );
  }, [model]);

  return (
    <Dialog
      fullWidth
      maxWidth='xl'
      open={open}
      onClose={onClose}
    >
      <form onSubmit={onSubmitHandler}>
        <DialogTitle>Select {plur(model)}</DialogTitle>
        <DialogContent>
          <div className='mb-2 text-right'>
            <TextField
              size='small'
              placeholder='Search'
              value={search}
              onChange={onChangeSearchHandler}
            />
          </div>

          {
            get(itemsInfo, 'data.items') && (
              <Table
                modelName={model}
                params={listParams}
                info={itemsInfo}
                fetchData={fetchData}
                headCells={headCells}
                renderRow={renderRow}
                multiple={false}
                hideToolbar
                showSelect
              />
            )
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" disabled={isFetching}>
            Cancel
          </Button>
          <Button type='submit' color="primary" disabled={isFetching || !listParams.getSelected().size}>
            Add
          </Button>
        </DialogActions>
      </form>

      <Backdrop open={isFetching} className='z-[9999]'>
        <CircularProgress />
      </Backdrop>
    </Dialog>
  );
}

DialogSelect.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  model: PropTypes.string.isRequired,
  payload: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

export default DialogSelect;
